import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { PageLoader } from '@/components/shared/loaders/PageLoader';
import { useUser } from '@/hooks/companyUser/useUser';
import { useAuthentication } from '@/hooks/user/useAuthentication';
import { useBranding } from '@/hooks/branding/useBranding';
import { useHeap } from '@/hooks/analytics/useHeap';
import {
  AuthorisedCustomerCompanyUserContext,
  AuthorisedCustomerCompanyUserContextValue,
} from '@/contexts/user/AuthorisedCustomerCompanyUserContext';
import { useIsGreyScoutAdmin } from '@/hooks/shared/useIsGreyScoutAdmin';

type UserGatewayProps = PropsWithChildren;

export const UserGateway = ({
  children,
}: UserGatewayProps) => {
  const [hasTriedToLogin, setHasTriedToLogin] = useState(false);

  const {
    user: authUser,
    loading: isAuthUserLoading,
  } = useAuthentication();

  const location = useLocation();

  const {
    branding,
  } = useBranding();

  const { identifyUser, trackLoggedIn } = useHeap();

  const userId = authUser?.gsUid;

  const {
    user: companyUser,
    isLoading: isCompanyUserLoading,
  } = useUser(userId ? String(userId) : undefined);

  const isGreyScoutAdmin = useIsGreyScoutAdmin();

  const companyUserId = companyUser?.linked?.company?.id;
  const brandingUserId = branding?.attributes.companyId;

  const isCustomerCompanyUserAuthorized = useMemo(() => (
    String(brandingUserId) === companyUserId || isGreyScoutAdmin
  ), [
    brandingUserId,
    companyUserId,
    isGreyScoutAdmin,
  ]);

  useEffect(() => {
    setHasTriedToLogin(false);
  }, [location]);

  useEffect(() => {
    if (companyUser && isCustomerCompanyUserAuthorized) {
      // Heap stubs are created in index.html before React mounts, so identify/track
      // calls made here are queued until HeapGateway loads the real SDK.
      identifyUser(companyUser.id, {
        email: companyUser.attributes.emailAddress,
        role: companyUser.attributes.role,
      });
      trackLoggedIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyUser?.id, isCustomerCompanyUserAuthorized]);

  const contextValue = useMemo<AuthorisedCustomerCompanyUserContextValue>(() => ({
    isCustomerCompanyUserAuthorized,
    isError: !isCustomerCompanyUserAuthorized && hasTriedToLogin,
    setHasTriedToLogin,
    hasTriedToLogin,
    companyUser,
  }), [
    isCustomerCompanyUserAuthorized,
    hasTriedToLogin,
    companyUser,
  ]);

  if (isAuthUserLoading || isCompanyUserLoading) {
    return (
      <PageLoader />
    );
  }

  return (
    <AuthorisedCustomerCompanyUserContext.Provider value={contextValue}>
      {children}
    </AuthorisedCustomerCompanyUserContext.Provider>
  );
};
